import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import {
  fetchAgent,
  fetchAgentAlerts,
  fetchAgentContacts,
  fetchAgentHardware,
  fetchAgentInstalledSoftware,
  fetchAgentPatches,
  fetchAgentRmmData,
  fetchAgentSoftware,
} from '@/api/agents'
import { AgentOverview } from '@/components/AgentOverview'
import { AgentHardware } from '@/components/AgentHardware'
import { AgentSecurity } from '@/components/AgentSecurity'
import { AgentNetwork } from '@/components/AgentNetwork'
import { WingetStatusCard } from '@/components/WingetStatusCard'
import { AgentMicrosoft365 } from '@/components/AgentMicrosoft365'
import { AgentEventLogs } from '@/components/AgentEventLogs'
import { AgentBackup } from '@/components/AgentBackup'
import { AgentExchange } from '@/components/AgentExchange'
import { DeviceHealthAnalysis } from '@/components/DeviceHealthAnalysis'
import { AlertStatusBadge } from '@/components/AlertStatusBadge'
import { SortableTable } from '@/components/SortableTable'
import { alertStatusSortValue } from '@/lib/alertStatus'
import { formatDateOnly, formatDateTime } from '@/lib/formatAgent'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { toolkitSummaryFromRmmData } from '@/lib/toolkitHealth'
import { showExchangeTabForAgent } from '@/lib/exchangeHealth'

const BASE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'security', label: 'Security' },
  { id: 'hardware', label: 'Hardware' },
  { id: 'network', label: 'Network' },
  { id: 'eventlogs', label: 'Event logs' },
  { id: 'microsoft365', label: 'Microsoft 365' },
  { id: 'backup', label: 'Backup' },
  { id: 'software', label: 'Software' },
  { id: 'patches', label: 'Patches' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'contacts', label: 'Contacts' },
]

const EXCHANGE_TAB = { id: 'exchange', label: 'Exchange' }

function buildTabs(agent, rmmData) {
  const tabs = [...BASE_TABS]
  if (showExchangeTabForAgent(agent, rmmData)) {
    const backupIndex = tabs.findIndex((item) => item.id === 'backup')
    tabs.splice(backupIndex, 0, EXCHANGE_TAB)
  }
  return tabs
}

function formatDate(iso) {
  return formatDateTime(iso)
}

export function AgentDetail() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [agent, setAgent] = useState(null)
  const [hardware, setHardware] = useState([])
  const [software, setSoftware] = useState([])
  const [installedSoftware, setInstalledSoftware] = useState(null)
  const [patches, setPatches] = useState([])
  const [contacts, setContacts] = useState([])
  const [alerts, setAlerts] = useState([])
  const [rmmData, setRmmData] = useState(null)
  const [toolkitSummary, setToolkitSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tabLoading, setTabLoading] = useState(false)
  const [error, setError] = useState('')

  const tabs = useMemo(() => buildTabs(agent, rmmData), [agent, rmmData])
  const requestedTab = searchParams.get('tab') || 'overview'
  const tab = tabs.some((t) => t.id === requestedTab) ? requestedTab : 'overview'

  function selectTab(nextTab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (!nextTab || nextTab === 'overview') {
          next.delete('tab')
        } else {
          next.set('tab', nextTab)
        }
        return next
      },
      { replace: true },
    )
  }

  useEffect(() => {
    if (!agent) return
    const knownIds = new Set([...BASE_TABS.map((t) => t.id), EXCHANGE_TAB.id])
    if (!knownIds.has(requestedTab)) {
      selectTab('overview')
      return
    }
    if (requestedTab === EXCHANGE_TAB.id && !tabs.some((t) => t.id === EXCHANGE_TAB.id)) {
      selectTab('overview')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- normalise invalid / unavailable ?tab=
  }, [agent, tabs, requestedTab])

  useEffect(() => {
    setLoading(true)
    fetchAgent(id)
      .then(setAgent)
      .catch(() => setError('Device not found.'))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!agent) return
    fetchAgentRmmData(id)
      .then((data) => {
        setRmmData(data)
        setToolkitSummary(toolkitSummaryFromRmmData(data))
      })
      .catch(() => {
        setRmmData(null)
        setToolkitSummary(null)
      })
  }, [id, agent])

  useEffect(() => {
    if (!agent) return
    setTabLoading(true)
    const loaders = {
      overview: () => fetchAgentHardware(id).then(setHardware),
      hardware: () =>
        Promise.all([
          fetchAgentHardware(id).then(setHardware),
          fetchAgentRmmData(id)
            .then((data) => {
              setRmmData(data)
              setToolkitSummary(toolkitSummaryFromRmmData(data))
            })
            .catch(() => {}),
        ]),
      software: () =>
        Promise.all([
          fetchAgentInstalledSoftware(id)
            .then(setInstalledSoftware)
            .catch(() => setInstalledSoftware(null)),
          fetchAgentSoftware(id)
            .then(setSoftware)
            .catch(() => setSoftware([])),
        ]),
      patches: () => fetchAgentPatches(id).then(setPatches),
      contacts: () => fetchAgentContacts(id).then(setContacts),
      alerts: () => fetchAgentAlerts(id).then(setAlerts),
      security: () =>
        fetchAgentRmmData(id).then((data) => {
          setRmmData(data)
          setToolkitSummary(toolkitSummaryFromRmmData(data))
        }),
      network: () =>
        Promise.all([
          fetchAgentHardware(id).then(setHardware),
          fetchAgentRmmData(id)
            .then((data) => {
              setRmmData(data)
              setToolkitSummary(toolkitSummaryFromRmmData(data))
            })
            .catch(() => {}),
        ]),
      microsoft365: () =>
        fetchAgentRmmData(id).then((data) => {
          setRmmData(data)
          setToolkitSummary(toolkitSummaryFromRmmData(data))
        }),
      eventlogs: () =>
        fetchAgentRmmData(id).then((data) => {
          setRmmData(data)
          setToolkitSummary(toolkitSummaryFromRmmData(data))
        }),
      backup: () =>
        fetchAgentRmmData(id).then((data) => {
          setRmmData(data)
          setToolkitSummary(toolkitSummaryFromRmmData(data))
        }),
      exchange: () =>
        fetchAgentRmmData(id).then((data) => {
          setRmmData(data)
          setToolkitSummary(toolkitSummaryFromRmmData(data))
        }),
    }
    if (loaders[tab]) {
      loaders[tab]().finally(() => setTabLoading(false))
    } else {
      setTabLoading(false)
    }
  }, [tab, id, agent])

  if (loading) {
    return <Skeleton className="h-64 w-full" />
  }

  if (error || !agent) {
    return (
      <Card>
        <CardContent className="pt-6 text-destructive">{error || 'Not found'}</CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/agents">
            <ArrowLeft className="h-4 w-4" />
            Back to devices
          </Link>
        </Button>
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">{agent.display_name || agent.hostname}</h2>
          <p className="text-sm text-muted-foreground">
            {agent.hostname}
            {agent.customer_name ? (
              <>
                {' · '}
                {agent.customer_id ? (
                  <Link
                    to={`/customers/${agent.customer_id}`}
                    className="text-primary hover:underline"
                  >
                    {agent.customer_name}
                  </Link>
                ) : (
                  agent.customer_name
                )}
              </>
            ) : null}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {agent.zabbix_url ? (
            <Button variant="outline" size="sm" asChild>
              <a href={agent.zabbix_url} target="_blank" rel="noreferrer">
                Open in Zabbix
              </a>
            </Button>
          ) : null}
          <Badge variant={agent.online_status ? 'success' : 'warning'}>
            {agent.online_status ? 'Online' : 'Offline'}
          </Badge>
        </div>
      </div>

      <DeviceHealthAnalysis agentId={agent.atera_id} />

      <div className="flex flex-wrap gap-2 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(t.id)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <AgentOverview
          agent={agent}
          hardware={hardware}
          rmmData={rmmData}
          toolkitSummary={toolkitSummary}
          onNavigateTab={selectTab}
          onAgentPatch={(patch) => setAgent((prev) => (prev ? { ...prev, ...patch } : prev))}
        />
      )}

      {tab === 'contacts' && (
        <ContactsPanel loading={tabLoading} contacts={contacts} />
      )}

      {tab === 'hardware' && (
        <AgentHardware
          agent={agent}
          hardware={hardware}
          snapshots={rmmData?.snapshots ?? []}
          loading={tabLoading}
        />
      )}

      {tab === 'software' && (
        <div className="space-y-4">
          <WingetStatusCard
            snapshot={
              installedSoftware
                ? {
                    payload: { programs: installedSoftware.programs },
                    collected_at: installedSoftware.collected_at,
                  }
                : null
            }
          />
          {installedSoftware?.programs?.length ? (
            <InstalledSoftwarePanel loading={tabLoading} data={installedSoftware} />
          ) : (
            <SoftwareInventoryPanel loading={tabLoading} software={software} />
          )}
        </div>
      )}

      {tab === 'patches' && (
        <PatchesPanel loading={tabLoading} patches={patches} />
      )}

      {tab === 'security' && (
        <AgentSecurity
          loading={tabLoading}
          data={rmmData}
          agent={agent}
          agentId={agent?.atera_id || id}
          onNavigateTab={selectTab}
        />
      )}

      {tab === 'network' && (
        <AgentNetwork
          agent={agent}
          hardware={hardware}
          loading={tabLoading}
          data={rmmData}
        />
      )}

      {tab === 'microsoft365' && (
        <AgentMicrosoft365 loading={tabLoading} data={rmmData} />
      )}

      {tab === 'eventlogs' && (
        <AgentEventLogs loading={tabLoading} data={rmmData} agentId={agent?.atera_id || id} />
      )}

      {tab === 'backup' && (
        <AgentBackup
          loading={tabLoading}
          data={rmmData}
          hostname={agent.hostname || agent.display_name}
        />
      )}

      {tab === 'exchange' && (
        <AgentExchange loading={tabLoading} data={rmmData} />
      )}

      {tab === 'alerts' && (
        <AlertsPanel loading={tabLoading} alerts={alerts} />
      )}
    </div>
  )
}

function ContactsPanel({ loading, contacts }) {
  const [open, setOpen] = useState(true)
  const count = Array.isArray(contacts) ? contacts.length : 0

  return (
    <CollapsePanel
      id="device-contacts"
      title="Contacts"
      collapsedSummary={
        count > 0
          ? `${count} contact${count === 1 ? '' : 's'}`
          : 'No linked contacts'
      }
      open={open}
      onOpenChange={setOpen}
    >
      <SortableTable
        loading={loading}
        empty="No linked contacts. Links are inferred from last logged-in user and Atera contact records at the same customer (run sync)."
        data={contacts}
        defaultSort={{ id: 'name', dir: 'asc' }}
        columns={[
          {
            id: 'name',
            label: 'Name',
            sortValue: (c) =>
              [c.first_name, c.last_name].filter(Boolean).join(' ').toLowerCase(),
            render: (c) =>
              [c.first_name, c.last_name].filter(Boolean).join(' ') || '—',
          },
          {
            id: 'email',
            label: 'Email',
            sortValue: (c) => c.email?.toLowerCase() ?? '',
            render: (c) => c.email || '—',
          },
          {
            id: 'phone',
            label: 'Phone',
            sortValue: (c) => (c.phone || c.mobile_phone || '').toLowerCase(),
            render: (c) => c.phone || c.mobile_phone || '—',
          },
          {
            id: 'job_title',
            label: 'Role',
            sortValue: (c) => c.job_title?.toLowerCase() ?? '',
            render: (c) => c.job_title || '—',
          },
          {
            id: 'match',
            label: 'Match',
            sortValue: (c) => `${c.is_primary ? 0 : 1}${c.match_method ?? ''}`,
            render: (c) =>
              c.is_primary ? `${c.match_method} (primary)` : c.match_method || '—',
          },
        ]}
      />
    </CollapsePanel>
  )
}

function AlertsPanel({ loading, alerts }) {
  const [open, setOpen] = useState(true)
  const count = Array.isArray(alerts) ? alerts.length : 0

  return (
    <CollapsePanel
      id="device-alerts"
      title="Alerts"
      collapsedSummary={
        count > 0
          ? `${count} alert${count === 1 ? '' : 's'}`
          : 'No alerts for this device'
      }
      open={open}
      onOpenChange={setOpen}
    >
      <SortableTable
        loading={loading}
        empty="No alerts for this device."
        data={alerts}
        defaultSort={{ id: 'created', dir: 'desc' }}
        columns={[
          {
            id: 'title',
            label: 'Title',
            sortValue: (a) => a.title?.toLowerCase() ?? '',
            render: (a) => a.title || '—',
          },
          {
            id: 'message',
            label: 'Detail',
            sortValue: (a) => a.message?.toLowerCase() ?? '',
            render: (a) => (
              <span className="block max-w-md truncate" title={a.message}>
                {a.message || '—'}
              </span>
            ),
          },
          {
            id: 'severity',
            label: 'Severity',
            sortValue: (a) => a.severity?.toLowerCase() ?? '',
            render: (a) => a.severity || '—',
          },
          {
            id: 'created',
            label: 'Created',
            sortValue: (a) => (a.created ? new Date(a.created).getTime() : null),
            render: (a) => formatDate(a.created),
          },
          {
            id: 'is_active',
            label: 'Alert status',
            sortValue: (a) => alertStatusSortValue(a.is_active),
            render: (a) => <AlertStatusBadge isActive={a.is_active} />,
          },
        ]}
      />
    </CollapsePanel>
  )
}

function InstalledSoftwarePanel({ loading, data }) {
  const [open, setOpen] = useState(true)
  const [typeFilter, setTypeFilter] = useState('all')
  const programs = data?.programs ?? []
  const isStore = (p) => (p.type || 'Desktop') === 'Store'
  const storeCount = programs.filter(isStore).length
  const desktopCount = programs.length - storeCount
  const filtered =
    typeFilter === 'desktop'
      ? programs.filter((p) => !isStore(p))
      : typeFilter === 'store'
        ? programs.filter(isStore)
        : programs

  const filters = [
    { id: 'all', label: `All (${programs.length})` },
    { id: 'desktop', label: `Desktop (${desktopCount})` },
    { id: 'store', label: `Store (${storeCount})` },
  ]

  const summary = [
    `${programs.length} program${programs.length === 1 ? '' : 's'}`,
    data?.collected_at ? `collected ${formatDate(data.collected_at)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <CollapsePanel
      id="device-installed-software"
      title="Installed software"
      collapsedSummary={summary || undefined}
      open={open}
      onOpenChange={setOpen}
      bodyClassName="space-y-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {programs.length} program{programs.length === 1 ? '' : 's'} installed
          {data?.collected_at ? ` · collected ${formatDate(data.collected_at)}` : ''}
        </p>
        <div className="inline-flex rounded-md border border-border p-0.5">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setTypeFilter(f.id)}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                typeFilter === f.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <SortableTable
        loading={loading}
        empty="No matching software."
        data={filtered}
        defaultSort={{ id: 'name', dir: 'asc' }}
        columns={[
          {
            id: 'name',
            label: 'Software',
            wrap: true,
            className: 'min-w-[140px] max-w-xs',
            sortValue: (s) => s.name?.toLowerCase() ?? '',
            render: (s) => s.name || '—',
          },
          {
            id: 'version',
            label: 'Version',
            wrap: true,
            className: 'max-w-[120px]',
            sortValue: (s) => s.version?.toLowerCase() ?? '',
            render: (s) => s.version || '—',
          },
          {
            id: 'publisher',
            label: 'Publisher',
            wrap: true,
            className: 'min-w-[120px] max-w-xs',
            sortValue: (s) => s.publisher?.toLowerCase() ?? '',
            render: (s) => s.publisher || '—',
          },
          {
            id: 'type',
            label: 'Type',
            sortValue: (s) => (s.type || 'Desktop').toLowerCase(),
            render: (s) => s.type || 'Desktop',
          },
          {
            id: 'install_date',
            label: 'Installed',
            sortValue: (s) => (s.install_date ? new Date(s.install_date).getTime() : null),
            render: (s) => formatDateTime(s.install_date),
          },
        ]}
      />
    </CollapsePanel>
  )
}

function SoftwareInventoryPanel({ loading, software }) {
  const [open, setOpen] = useState(true)
  const count = Array.isArray(software) ? software.length : 0

  return (
    <CollapsePanel
      id="device-software-inventory"
      title="Installed software"
      collapsedSummary={
        count > 0
          ? `${count} item${count === 1 ? '' : 's'} from Atera inventory`
          : 'No software reported yet'
      }
      open={open}
      onOpenChange={setOpen}
    >
      <SortableTable
        loading={loading}
        empty="No installed software reported yet. Deploy the installed-software RMM script from Atera to populate this tab."
        data={software}
        defaultSort={{ id: 'name', dir: 'asc' }}
        columns={[
          {
            id: 'name',
            label: 'Name',
            sortValue: (s) => s.name?.toLowerCase() ?? '',
            render: (s) => s.name || '—',
          },
          {
            id: 'version',
            label: 'Version',
            sortValue: (s) => s.version?.toLowerCase() ?? '',
            render: (s) => s.version || '—',
          },
          {
            id: 'publisher',
            label: 'Publisher',
            sortValue: (s) => s.publisher?.toLowerCase() ?? '',
            render: (s) => s.publisher || '—',
          },
          {
            id: 'install_path',
            label: 'Install path',
            sortValue: (s) => s.install_path?.toLowerCase() ?? '',
            render: (s) => (
              <span className="block max-w-md truncate" title={s.install_path}>
                {s.install_path || '—'}
              </span>
            ),
          },
        ]}
      />
    </CollapsePanel>
  )
}

function PatchesPanel({ loading, patches }) {
  const [open, setOpen] = useState(true)
  const count = Array.isArray(patches) ? patches.length : 0

  return (
    <CollapsePanel
      id="device-patches"
      title="Patches"
      collapsedSummary={
        count > 0
          ? `${count} patch${count === 1 ? '' : 'es'}`
          : 'No patch data yet'
      }
      open={open}
      onOpenChange={setOpen}
    >
      <SortableTable
        loading={loading}
        empty="No patch data yet. Run a sync from Admin after the portal update; patches use Atera DeviceGuid."
        data={patches}
        defaultSort={{ id: 'title', dir: 'asc' }}
        columns={[
          {
            id: 'title',
            label: 'Title',
            sortValue: (p) => p.title?.toLowerCase() ?? '',
            render: (p) => p.title || '—',
          },
          {
            id: 'severity',
            label: 'Severity',
            sortValue: (p) => p.severity?.toLowerCase() ?? '',
            render: (p) => p.severity || '—',
          },
          {
            id: 'status',
            label: 'Status',
            sortValue: (p) => p.status?.toLowerCase() ?? '',
            render: (p) => <span className="capitalize">{p.status || '—'}</span>,
          },
          {
            id: 'kb_number',
            label: 'KB',
            sortValue: (p) => p.kb_number ?? '',
            render: (p) => p.kb_number || '—',
          },
          {
            id: 'install_date',
            label: 'Installed',
            sortValue: (p) => (p.install_date ? new Date(p.install_date).getTime() : null),
            render: (p) => formatDateOnly(p.install_date),
          },
        ]}
      />
    </CollapsePanel>
  )
}

